import _ from "underscore";
import { ComponentCommonImpl } from "../../component/model/ComponentCommonImpl";
import { createBuiltCampaignComponentSettings } from "../setting/model/factory/createBuiltCampaignComponentSettings";
import { isBuiltCampaignComponentVariable, } from "../setting/model/BuiltCampaignComponentVariable";
import { isBuiltCampaignComponentSocket, } from "../setting/model/BuiltCampaignComponentSocket";
import Strings from "src/shared/Strings";
import { useDeveloperMode } from "src/shared/store/me/composables";
import { uniq } from "lodash";
const calculateDefaultPriorityBasedOnResponsePosition = (variableResponse, index) => {
    if (variableResponse.priority === "100") {
        return `100.${index}`;
    }
    return variableResponse.priority;
};
const createSettings = (responses, context) => {
    const output = {};
    const responsesModifiedWithPriority = responses.map((response, index) => {
        return Object.assign(Object.assign({}, response), { priority: calculateDefaultPriorityBasedOnResponsePosition(response, index) });
    });
    createBuiltCampaignComponentSettings(responsesModifiedWithPriority, context).forEach((setting) => {
        output[setting.name] = setting;
    });
    return output;
};
export const ENABLED_VARIABLE_NAME = "enabled";
const ADMIN_UI_COLOR_VARIABLE_NAME = "adminUIColor";
const ADMIN_UI_ICON_VARIABLE_NAME = "adminUIIcon";
class BuiltComponent {
    constructor(builtComponentResponse) {
        this.id = builtComponentResponse.id;
        this.source_client_id = builtComponentResponse.source_client_id;
        this.name = builtComponentResponse.name;
        this.display_name = builtComponentResponse.display_name;
        this.type = builtComponentResponse.type;
        this.upload_version = builtComponentResponse.upload_version;
        this.campaign_id = builtComponentResponse.campaign_id;
        this.description = builtComponentResponse.description;
        this.tags = builtComponentResponse.tags;
        this.absolute_names = builtComponentResponse.absolute_names;
        this.component_ids = builtComponentResponse.component_ids;
        this.created_date = builtComponentResponse.created_date;
        this.updated_date = builtComponentResponse.updated_date;
        this.settings = createSettings(builtComponentResponse.variables, {
            componentId: this.id,
            campaignId: this.campaign_id,
        });
        this.componentCommonImpl = new ComponentCommonImpl(builtComponentResponse);
        this.deprecated_types = this.componentCommonImpl.deprecated_types;
        this.installed_into_socket =
            builtComponentResponse.installed_into_socket;
        this.facets = builtComponentResponse.facets || [];
    }
    getParentComponentId() {
        return this.component_ids[0];
    }
    findSetting(name) {
        return this.settings[name];
    }
    listSettings() {
        const { developerMode } = useDeveloperMode();
        const settings = _.values(this.settings);
        if (this.isEnableable() && !developerMode.value) {
            return settings.filter((setting) => setting.name !== ENABLED_VARIABLE_NAME);
        }
        return settings;
    }
    findVariable(name) {
        const setting = this.findSetting(name);
        if (!setting || !isBuiltCampaignComponentVariable(setting)) {
            return undefined;
        }
        return setting;
    }
    listVariables() {
        const output = [];
        this.listSettings().forEach((setting) => {
            if (isBuiltCampaignComponentVariable(setting)) {
                output.push(setting);
            }
        });
        return output;
    }
    findSocket(names) {
        const nameArray = Array.isArray(names) ? names : [names];
        for (const name of nameArray) {
            const setting = this.findSetting(name);
            if (setting && isBuiltCampaignComponentSocket(setting)) {
                return setting;
            }
        }
        return undefined;
    }
    listSockets() {
        const output = [];
        this.listSettings().forEach((setting) => {
            if (isBuiltCampaignComponentSocket(setting)) {
                output.push(setting);
            }
        });
        return output;
    }
    listFacets() {
        return this.facets;
    }
    isRoot() {
        return this.componentCommonImpl.isRoot();
    }
    getDisplayName() {
        return this.display_name || Strings.capitalize(this.name);
    }
    getDescription() {
        return this.description;
    }
    hasCreative() {
        return this.componentCommonImpl.hasCreative();
    }
    isEnableable() {
        const variable = this.findVariable(ENABLED_VARIABLE_NAME);
        return (variable === null || variable === void 0 ? void 0 : variable.type) === "BOOLEAN";
    }
    isEnabled() {
        const variable = this.findVariable(ENABLED_VARIABLE_NAME);
        if (!variable || variable.type !== "BOOLEAN") {
            return true;
        }
        return variable.getValue("default") === true;
    }
    getColor() {
        const variable = this.findVariable(ADMIN_UI_COLOR_VARIABLE_NAME);
        if (!variable || variable.type !== "COLOR") {
            return null;
        }
        const defaultValue = variable.getValue("default");
        if (typeof defaultValue === "string") {
            return defaultValue;
        }
        return null;
    }
    getIcon() {
        const variable = this.findVariable(ADMIN_UI_ICON_VARIABLE_NAME);
        if (!variable) {
            return null;
        }
        const defaultValue = variable.getValue("default");
        if (typeof defaultValue === "string") {
            return defaultValue;
        }
        return null;
    }
    getImageUrl() {
        // TODO - convention from integrations that needs to be standardized
        const iconVariable = this.findVariable("icon");
        const logoVariable = this.findVariable("logo");
        if (!iconVariable && !logoVariable) {
            return null;
        }
        if (iconVariable) {
            const defaultValue = iconVariable.getValue("default");
            if (typeof defaultValue === "string") {
                return defaultValue;
            }
        }
        if (logoVariable) {
            const defaultValue = logoVariable.getValue("default");
            if (typeof defaultValue === "string") {
                return defaultValue;
            }
        }
        return null;
    }
    getTypeCategories() {
        // TODO - convention from integrations that needs to be standardized
        const categories = [];
        const typeCategoriesVariable = this.findVariable("categories.type");
        if (typeCategoriesVariable) {
            const typeCategories = typeCategoriesVariable.getValue("default");
            if (Array.isArray(typeCategories)) {
                categories.push(...typeCategories);
            }
            else if (typeof typeCategories === "string" &&
                typeCategories.length > 0) {
                categories.push(...typeCategories.split(",").map((cat) => cat.trim()));
            }
        }
        return categories;
    }
    getFunctionalityCategories() {
        // TODO - convention from integrations that needs to be standardized
        const categories = [];
        const functionalityCategoriesVariable = this.findVariable("categories.functionality");
        if (functionalityCategoriesVariable) {
            const functionalityCategories = functionalityCategoriesVariable.getValue("default");
            if (Array.isArray(functionalityCategories)) {
                categories.push(...functionalityCategories);
            }
            else if (typeof functionalityCategories === "string" && functionalityCategories.length > 0) {
                categories.push(...functionalityCategories.split(",").map(cat => cat.trim()));
            }
        }
        return categories;
    }
    getCategories() {
        // TODO - convention from integrations that needs to be standardized
        const categories = [];
        const categoriesVariable = this.findVariable("categories");
        if (categoriesVariable) {
            const categoriesValue = categoriesVariable.getValue("default");
            if (typeof categoriesValue === "string" && categoriesValue.length > 0) {
                categories.push(...categoriesValue.split(",").map(cat => cat.trim()));
            }
        }
        categories.push(...this.getTypeCategories());
        categories.push(...this.getFunctionalityCategories());
        return uniq(categories);
    }
}
export default BuiltComponent;
